import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Map, { Marker } from 'react-map-gl/mapbox';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
  IconButton,
  TextField,
  MenuItem,
} from '@mui/material';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import MyLocationRoundedIcon from '@mui/icons-material/MyLocationRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';
import axios from '../../api/axiosInstance';
import PageHeader from '../../components/common/PageHeader';

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function hasCoordinates(site) {
  return Number.isFinite(Number(site.latitude)) && Number.isFinite(Number(site.longitude));
}

function getInitialViewState(points) {
  if (!points.length) {
    return { longitude: 55.5364, latitude: -21.1151, zoom: 8.2 };
  }

  const lng = points.reduce((sum, point) => sum + Number(point.longitude), 0) / points.length;
  const lat = points.reduce((sum, point) => sum + Number(point.latitude), 0) / points.length;
  return { longitude: lng, latitude: lat, zoom: points.length === 1 ? 13 : 9 };
}

export default function OrganizationSitesPage() {
  const mapRef = useRef(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [rootFilter, setRootFilter] = useState('all');

  const rootOptions = useMemo(() => {
    const values = Array.from(new Set(
      sites
        .map((site) => String(site.rootOrganization || '').trim())
        .filter(Boolean)
    )).sort((a, b) => a.localeCompare(b, 'fr'));
    return values;
  }, [sites]);

  const filteredSites = useMemo(() => {
    if (rootFilter === 'all') return sites;
    return sites.filter((site) => String(site.rootOrganization || '').trim() === rootFilter);
  }, [sites, rootFilter]);

  const mappedSites = useMemo(() => filteredSites.filter(hasCoordinates), [filteredSites]);
  const missingCoordinates = filteredSites.length - mappedSites.length;

  const loadSites = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get('/api/ik/sites');
      setSites(Array.isArray(data?.data) ? data.data : []);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Impossible de charger les sites clients.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSyncStatus = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/ik/sites/sync/status');
      setSyncStatus(data || null);
      return data;
    } catch (err) {
      setSyncStatus(null);
      return null;
    }
  }, []);

  const startSync = async () => {
    setSyncing(true);
    setMessage('');
    setError('');
    try {
      const { data } = await axios.post('/api/ik/sites/sync', {});
      setSyncStatus(data?.status || null);
      setMessage(data?.message || 'Synchronisation des sites clients lancée. Cette opération peut prendre plusieurs minutes.');
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Impossible de lancer la synchronisation des sites clients.');
    } finally {
      setSyncing(false);
      loadSyncStatus();
    }
  };

  useEffect(() => {
    loadSites();
    loadSyncStatus();
  }, [loadSites, loadSyncStatus]);

  useEffect(() => {
    if (!syncStatus?.running) return undefined;
    const interval = setInterval(async () => {
      const status = await loadSyncStatus();
      if (status && !status.running) loadSites();
    }, 5000);
    return () => clearInterval(interval);
  }, [loadSites, loadSyncStatus, syncStatus?.running]);

  const fitMapToPoints = useCallback(() => {
    if (!mapRef.current || mappedSites.length === 0) return;
    const bounds = mappedSites.reduce((acc, site) => {
      const lng = Number(site.longitude);
      const lat = Number(site.latitude);
      return [
        [Math.min(acc[0][0], lng), Math.min(acc[0][1], lat)],
        [Math.max(acc[1][0], lng), Math.max(acc[1][1], lat)],
      ];
    }, [[Infinity, Infinity], [-Infinity, -Infinity]]);

    mapRef.current.fitBounds(bounds, {
      padding: 70,
      maxZoom: 13,
      duration: 700,
    });
  }, [mappedSites]);

  useEffect(() => {
    const timeout = setTimeout(fitMapToPoints, 250);
    return () => clearTimeout(timeout);
  }, [fitMapToPoints]);


  const focusSiteOnMap = useCallback((site) => {
    if (!site || !hasCoordinates(site) || !mapRef.current) return;
    setSelectedSiteId(String(site._id || site.hudu_id || ''));
    mapRef.current.flyTo({
      center: [Number(site.longitude), Number(site.latitude)],
      zoom: 15,
      duration: 900,
      essential: true,
    });
  }, []);

  const rows = useMemo(() => filteredSites.map((site) => ({
    ...site,
    coordinatesStatus: hasCoordinates(site) ? 'OK' : 'Adresse non géocodée',
  })), [filteredSites]);

  const columns = useMemo(() => [
    {
      id: 'mapAction',
      header: 'Carte',
      size: 70,
      enableColumnFilter: false,
      enableSorting: false,
      Cell: ({ row }) => {
        const canLocate = hasCoordinates(row.original);
        return (
          <Tooltip title={canLocate ? 'Voir sur la carte' : 'Adresse sans coordonnées'}>
            <span>
              <IconButton
                size="small"
                color={String(selectedSiteId) === String(row.original._id || row.original.hudu_id || '') ? 'primary' : 'default'}
                onClick={() => focusSiteOnMap(row.original)}
                disabled={!canLocate}
              >
                <MyLocationRoundedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: 'organizationName',
      header: 'Client',
      size: 220,
      Cell: ({ cell }) => <Typography variant="body2" sx={{ fontWeight: 700 }}>{cell.getValue() || '-'}</Typography>,
    },
    { accessorKey: 'name', header: 'Site', size: 220 },
    {
      accessorKey: 'address',
      header: 'Adresse',
      size: 360,
      Cell: ({ cell }) => (
        <Box component="span" sx={{ display: 'block', maxWidth: 460, whiteSpace: 'normal' }}>
          {cell.getValue() || '-'}
        </Box>
      ),
    },
    {
      accessorKey: 'geocodeQuery',
      header: 'Recherche carte',
      size: 360,
      Cell: ({ cell }) => (
        <Box component="span" sx={{ display: 'block', maxWidth: 460, whiteSpace: 'normal', color: 'text.secondary' }}>
          {cell.getValue() || '-'}
        </Box>
      ),
    },
    { accessorKey: 'organizationParent', header: 'Parent', size: 180 },
    { accessorKey: 'rootOrganization', header: 'Société', size: 140 },
    {
      accessorKey: 'coordinatesStatus',
      header: 'Carte',
      size: 150,
      Cell: ({ row }) => (
        <Chip
          size="small"
          color={hasCoordinates(row.original) ? 'success' : 'default'}
          variant={hasCoordinates(row.original) ? 'outlined' : 'filled'}
          label={hasCoordinates(row.original) ? 'Positionné' : 'Sans point'}
        />
      ),
    },
    {
      accessorKey: 'geocodeError',
      header: 'Erreur Mapbox',
      size: 240,
      Cell: ({ cell }) => cell.getValue() || '-',
    },
    {
      accessorKey: 'updatedAt',
      header: 'Dernière maj',
      size: 170,
      Cell: ({ cell }) => formatDateTime(cell.getValue()),
    },
  ], [focusSiteOnMap, selectedSiteId]);

  const table = useMaterialReactTable({
    columns,
    data: rows,
    localization: MRT_Localization_FR,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    enableColumnResizing: true,
    enableGrouping: true,
    initialState: {
      showColumnFilters: true,
      showGlobalFilter: true,
      density: 'compact',
      pagination: { pageIndex: 0, pageSize: 25 },
    },
    state: { isLoading: loading },
    getRowId: (row) => String(row._id || row.hudu_id || `${row.organizationName}-${row.name}`),
  });

  const syncSeverity = syncStatus?.running ? 'info' : syncStatus?.status === 'error' ? 'error' : 'success';

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={2.5}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <PageHeader section="Clients" title="Sites" sx={{ flex: 1 }} />
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={loadSites} disabled={loading || syncing}>
              Actualiser
            </Button>
            <Button
              variant="contained"
              startIcon={syncing || syncStatus?.running ? <CircularProgress size={16} color="inherit" /> : <SyncRoundedIcon />}
              onClick={startSync}
              disabled={syncing || syncStatus?.running}
            >
              Synchroniser Sites Client
            </Button>
          </Stack>
        </Stack>

        {message && <Alert severity="success">{message}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <TextField
              select
              size="small"
              label="Société"
              value={rootFilter}
              onChange={(event) => {
                setRootFilter(event.target.value);
                setSelectedSiteId(null);
              }}
              sx={{ minWidth: 240 }}
            >
              <MenuItem value="all">Toutes les sociétés</MenuItem>
              {rootOptions.map((root) => (
                <MenuItem key={root} value={root}>{root}</MenuItem>
              ))}
            </TextField>
            <Typography variant="body2" color="text.secondary">
              {filteredSites.length} site(s) affiché(s) sur {sites.length}
            </Typography>
          </Stack>
        </Paper>

        {syncStatus ? (
          <Alert severity={syncSeverity}>
            <Stack spacing={0.25}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {syncStatus.message || 'Synchronisation sites clients prête.'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {syncStatus.running
                  ? `Lancé le ${formatDateTime(syncStatus.startedAt)}. Cette synchronisation peut prendre plusieurs minutes, merci de patienter.`
                  : `Dernier lancement : ${formatDateTime(syncStatus.startedAt || syncStatus.lastRunAt)} · Fin : ${formatDateTime(syncStatus.finishedAt)}`}
              </Typography>
            </Stack>
          </Alert>
        ) : null}

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip label={`${filteredSites.length} site(s)`} />
              <Chip label={`${mappedSites.length} point(s) carte`} color="success" variant="outlined" />
              {missingCoordinates > 0 ? <Chip label={`${missingCoordinates} sans coordonnées`} variant="outlined" /> : null}
            </Stack>
            <Box sx={{ height: 430, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
              {process.env.REACT_APP_MAPBOX_TOKEN ? (
                <Map
                  ref={mapRef}
                  mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
                  initialViewState={getInitialViewState(mappedSites)}
                  onLoad={fitMapToPoints}
                  style={{ width: '100%', height: '100%' }}
                  mapStyle="mapbox://styles/mapbox/streets-v12"
                >
                  {mappedSites.map((site) => {
                    const isSelected = String(selectedSiteId) === String(site._id || site.hudu_id || '');
                    return (
                    <Marker key={site._id || site.hudu_id} longitude={Number(site.longitude)} latitude={Number(site.latitude)} anchor="bottom">
                      <Tooltip title={`${site.organizationName || 'Client'} - ${site.name || 'Site'}`}>
                        <Box
                          onClick={() => focusSiteOnMap(site)}
                          sx={{
                            width: isSelected ? 38 : 30,
                            height: isSelected ? 38 : 30,
                            borderRadius: '50% 50% 50% 0',
                            transform: 'rotate(-45deg)',
                            bgcolor: isSelected ? '#f59e0b' : '#0f7b4d',
                            border: '2px solid #fff',
                            boxShadow: isSelected ? 5 : 3,
                            display: 'grid',
                            placeItems: 'center',
                            cursor: 'pointer',
                            transition: 'all 160ms ease',
                          }}
                        >
                          <PlaceRoundedIcon sx={{ color: '#fff', fontSize: 18, transform: 'rotate(45deg)' }} />
                        </Box>
                      </Tooltip>
                    </Marker>
                    );
                  })}
                </Map>
              ) : (
                <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                  <Typography color="text.secondary">Token Mapbox non configuré.</Typography>
                </Stack>
              )}
            </Box>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <MaterialReactTable table={table} />
        </Paper>
      </Stack>
    </Box>
  );
}
